import { getPrinters } from "@/lib/usePrecontoPrinter";
import { pickPrinter, printerUsable, comandaPrintersForStore } from "@/lib/pickPrinter";
import { getActiveStoreId } from "@/lib/storeContext";
import { buildReceiptEscpos, buildComandaEscpos } from "@/lib/escpos";
import { sendRawTcp, isNativePrint, printChannelHint } from "@/lib/printTransport";

export const printReceiptViaQz = async ({ tenant, order, kind }) => {
  const raw = await getPrinters();
  const printers = Array.isArray(raw) ? raw : [];
  const sid = order?.store_id || getActiveStoreId();
  const wantsPreconto = kind === "preconto" || kind === "management";
  const role = wantsPreconto ? "preconto" : "fiscal";
  const chosen = pickPrinter(printers, role, sid);
  const fallbackPre = pickPrinter(printers, "preconto", sid);
  const target = printerUsable(chosen) ? chosen : (!wantsPreconto && printerUsable(fallbackPre) ? fallbackPre : null);
  if (!printerUsable(target)) {
    if (chosen && chosen.model === "simulator") return { ok: false, reason: "simulator" };
    return { ok: false, reason: "no_printer" };
  }
  const r = await sendRawTcp({
    host: target.ip,
    port: target.port || 9100,
    data: buildReceiptEscpos({ tenant, order, kind }),
    encoding: "ISO-8859-1",
  });
  if (!r.ok) return { ok: false, reason: r.reason || "send_failed", detail: r.detail, via: r.via };
  return { ok: true, printer: target.name, via: r.via };
};

const STATION_KEYS = {
  kitchen: ["kitchen", "cucina"],
  pizzeria: ["pizzeria", "pizza"],
  bar: ["bar", "caffe", "caffè"],
};

const lineStation = (line) => String(line?.print_station || line?.print_to || "").toLowerCase().trim();

const printerMatchesStation = (printer, station) => {
  if (!station) return false;
  const pname = String(printer.name || "").toLowerCase();
  const pstat = String(printer.station || "").toLowerCase();
  if (pstat === station || pname === station) return true;
  const keys = STATION_KEYS[station] || [station];
  if (keys.includes(pstat)) return true;
  return keys.some((k) => pname.includes(k));
};

const pickComandaPrinter = (comandas, line) => {
  const station = lineStation(line);
  const byName = station ? comandas.find((p) => String(p.name || "").toLowerCase() === station) : null;
  if (byName) return byName;
  const byStation = station ? comandas.find((p) => printerMatchesStation(p, station)) : null;
  if (byStation) return byStation;
  const byCat = line.category_id ? comandas.find((p) => (p.categories || []).includes(line.category_id)) : null;
  if (byCat) return byCat;
  return comandas[0];
};

export const printComandeViaQz = async ({ order, tenant, tableName, roomName, operator }) => {
  const printers = await getPrinters();
  const comandas = comandaPrintersForStore(printers, order?.store_id || getActiveStoreId());
  if (!comandas.length) return { ok: false, reason: "no_printer" };
  const groups = new Map();
  for (const l of (Array.isArray(order.lines) ? order.lines : [])) {
    const target = pickComandaPrinter(comandas, l);
    if (!groups.has(target.id)) groups.set(target.id, { printer: target, lines: [] });
    groups.get(target.id).lines.push(l);
  }
  const printed = [];
  const op = operator || order.operator_name || order.last_operator_name || "";
  for (const { printer, lines } of groups.values()) {
    const r = await sendRawTcp({
      host: printer.ip,
      port: printer.port || 9100,
      data: buildComandaEscpos({
        station: printer.station || printer.name,
        tableName,
        roomName,
        orderNumber: order.order_number,
        lines,
        notes: order.notes,
        operator: op,
        tenant: tenant || {},
      }),
      encoding: "ISO-8859-1",
    });
    if (!r.ok) return { ok: false, reason: r.reason || "send_failed", detail: r.detail, via: r.via, printer: printer.name };
    printed.push({ station: printer.name, printer: printer.name, via: r.via });
  }
  return { ok: true, printed, via: (await isNativePrint()) ? "android" : "qz" };
};

export const thermalPrintError = (res) => {
  const map = {
    simulator: "Stampante in simulazione: non si stampa su A4. In negozio imposta IP reale della termica sulla stessa LAN del tablet.",
    no_printer: "Nessuna stampante di rete configurata (IP). Aggiungila in Stampanti.",
    qz_offline: printChannelHint(),
    send_failed: `Invio alla stampante fallito${res.detail ? `: ${res.detail}` : ""}`,
  };
  return map[res?.reason] || "Stampa termica non riuscita";
};
